import { formatDate } from '@/utils/date'
import { computed, reactive } from 'vue'
import { usePermissions } from '@/use/permissions'
import { useFetch } from './fetch'
import { GetPospalCoupon } from '@/api/pospal'
import { concat, get, includes } from 'lodash'
import { useShop } from './shop'
import { GetCyjPosCouponList } from '@/api/cyjPos'
import { GetCouponOpentix } from '@/api/opentix'
import { GetTkhspcCoupon } from '@/api/customModule'
import { GetXigogoCoupon } from '@/api/xigogo'
import { GetCoupon } from '@/api/lottery/coupon'

export const useCoupon = ({ fetchLimit } = { fetchLimit: 300 }) => {
  const { checkAction } = usePermissions()
  const { fetchAll } = useFetch()
  const { shopId } = useShop()
  const couponTypeOrder = ['coupon', 'exchange', 'pospal', 'opentix', 'tkhspc', 'cyjPos', 'xigogo']
  const couponResources = reactive({
    coupon: [],
    exchange: [],
    pospal: [],
    opentix: [],
    tkhspc: [],
    cyjPos: [],
    xigogo: [],
  })
  const couponFeatures = computed(() => {
    return {
      coupon: checkAction('planModule.coupon.enable'),
      exchange: checkAction('planModule.couponExchange.enable'),
      pospal: checkAction('admin.couponPospal.find'),
      opentix: checkAction('admin.couponOpentix.find'),
      tkhspc: checkAction('admin.couponTkhspc.find'),
      cyjPos: checkAction('admin.couponCyjPos.find'),
      xigogo: checkAction('admin.couponXigogo.find'),
    }
  })
  const enabledCouponTypes = computed(() => couponTypeOrder.filter((type) => couponFeatures.value[type]))
  const disabledCouponTypes = computed(() => couponTypeOrder.filter((type) => !couponFeatures.value[type]))
  const hasEnabledCouponTypes = computed(() => enabledCouponTypes.value.length > 0)

  const composeAllCouponResources = computed(() => {
    return concat([],
      couponResources.coupon,
      couponResources.exchange,
      couponResources.pospal,
      couponResources.opentix,
      couponResources.tkhspc,
      couponResources.cyjPos,
      couponResources.xigogo,
    )
  })

  const getAllCouponResources = async (getList = couponTypeOrder) => {
    const callList = []
    if (get(couponFeatures.value, 'pospal') && includes(getList, 'pospal')) {
      callList.push(async () => {
        await fetchAll(GetPospalCoupon, {
          shopId: shopId.value,
          limit: fetchLimit,
        }, (res) => {
          couponResources.pospal = res
        })
      })
    }
    if (get(couponFeatures.value, 'opentix') && includes(getList, 'opentix')) {
      callList.push(async () => {
        await fetchAll(GetCouponOpentix, {
          shopId: shopId.value,
          limit: fetchLimit,
        }, (res) => {
          couponResources.opentix = res
        })
      })
    }
    if (get(couponFeatures.value, 'tkhspc') && includes(getList, 'tkhspc')) {
      callList.push(async () => {
        await fetchAll(GetTkhspcCoupon, {
          shopId: shopId.value,
          limit: fetchLimit,
        }, (res) => {
          couponResources.tkhspc = res
        })
      })
    }
    if (get(couponFeatures.value, 'cyjPos') && includes(getList, 'cyjPos')) {
      callList.push(async () => {
        await fetchAll(GetCyjPosCouponList, {
          shopId: shopId.value,
          limit: fetchLimit,
        }, (res) => {
          couponResources.cyjPos = res
        })
      })
    }
    if (get(couponFeatures.value, 'xigogo') && includes(getList, 'xigogo')) {
      callList.push(async () => {
        await fetchAll(GetXigogoCoupon, {
          shopId: shopId.value,
          limit: fetchLimit,
        }, (res) => {
          couponResources.xigogo = res
        })
      })
    }
    const hasCouponReq = get(couponFeatures.value, 'coupon') && includes(getList, 'coupon')
    const hasExchangeReq = get(couponFeatures.value, 'exchange') && includes(getList, 'exchange')

    if (hasCouponReq || hasExchangeReq) {
      callList.push(async () => {
        await fetchAll(GetCoupon, {
          shopId: shopId.value,
          type: 'all',
          limit: fetchLimit,
        }, (res) => {
          couponResources.coupon = res.filter(i => i.type === 'coupon')
          couponResources.exchange = res.filter(i => i.type === 'exchange')
        })
      })
    }

    await Promise.allSettled(callList.map(fn => fn()))
  }

  const formatExpPreviewText = ({ isExp, expType, expDay, endOfPeriodUnit, expAt, specifyExpiredDate }) => {
    if (isExp === false) return '無期限'
    if (expType === 'expAt' || expType === 'specify') return formatDate(expAt || specifyExpiredDate)
    else if (expType === 'expDay') return `領取後 ${expDay || '-'} 天`
    else if (expType === 'endOfPeriod') {
      if (endOfPeriodUnit === 'month') return '領取當月最後一天'
    }
    return '-'
  }
  return {
    formatExpPreviewText,
    getAllCouponResources,
    couponResources,
    couponFeatures,
    enabledCouponTypes,
    disabledCouponTypes,
    hasEnabledCouponTypes,
    composeAllCouponResources,
  }
}
